"use client";

import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@/components/ui/modal";
import { Icon } from "@/components/ui/icon";
import { PlanIcon } from "@/components/ui/plan-icon";
import { customInstance } from "@/lib/api/client";
import { StripeElementsProvider } from "./stripe-elements-provider";
import type { PlanResponse } from "../hooks/use-subscription";

interface ReactivationFlowProps {
  open: boolean;
  onClose: () => void;
  plans: PlanResponse[];
}

interface SeatAssignment {
  user_id: string;
  email: string;
  full_name: string | null;
  is_admin: boolean;
}

type Step = "plan" | "members" | "payment" | "processing";

const STEPS = ["Pick Plan", "Select Members", "Payment"] as const;

const stepTransition = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const },
} as const;

export function ReactivationFlow({
  open,
  onClose,
  plans,
}: ReactivationFlowProps) {
  const displayPlans = plans.filter((p) => p.slug !== "free");

  const defaultPlan =
    displayPlans.find((p) => p.slug === "growth") ?? displayPlans[0];
  const defaultPrice = defaultPlan ? (defaultPlan.price_monthly_cents ?? 0) : 0;

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      dismissible={false}
    >
      <ModalContent size="lg">
        <StripeElementsProvider amount={defaultPrice || 100}>
          <ReactivationInner plans={displayPlans} onClose={onClose} />
        </StripeElementsProvider>
      </ModalContent>
    </Modal>
  );
}

function ReactivationInner({
  plans,
  onClose,
}: {
  plans: PlanResponse[];
  onClose: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>("plan");
  const [selectedPlan, setSelectedPlan] = useState<PlanResponse | null>(
    plans.find((p) => p.slug === "growth") ?? plans[0] ?? null,
  );
  const [cadence, setCadence] = useState<"monthly" | "annual">("monthly");
  const [seatCount, setSeatCount] = useState(1);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set(),
  );
  const [members, setMembers] = useState<SeatAssignment[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stepIndex =
    step === "plan" ? 0 : step === "members" ? 1 : step === "payment" ? 2 : 2;

  const fetchMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const data = await customInstance<SeatAssignment[]>(
        "/api/billing/seats/assignments",
        { method: "GET" },
      );
      setMembers(data);
      const adminIds = new Set(
        data.filter((m) => m.is_admin).map((m) => m.user_id),
      );
      setSelectedUserIds(adminIds);
    } catch {
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  useEffect(() => {
    if (step === "members") {
      fetchMembers();
    }
  }, [step, fetchMembers]);

  const pricePerSeat = selectedPlan
    ? cadence === "annual"
      ? Math.round((selectedPlan.price_annual_cents ?? 0) / 12)
      : (selectedPlan.price_monthly_cents ?? 0)
    : 0;
  const totalAmount = pricePerSeat * seatCount;

  const toggleMember = (userId: string, isAdmin: boolean) => {
    if (isAdmin) return;
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else if (next.size < seatCount) {
        next.add(userId);
      }
      return next;
    });
  };

  const handleReactivate = async () => {
    if (!stripe || !elements || !selectedPlan) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message ?? "Payment validation failed.");
        setIsSubmitting(false);
        return;
      }

      const result = await customInstance<{
        type: "payment" | "setup";
        client_secret: string;
        subscription_id: string;
      }>("/api/billing/reactivate", {
        method: "POST",
        body: JSON.stringify({
          plan_slug: selectedPlan.slug,
          billing_cadence: cadence,
          seat_count: seatCount,
          user_ids_to_activate: Array.from(selectedUserIds),
        }),
        headers: { "Content-Type": "application/json" },
      });

      const returnUrl = `${window.location.origin}/settings/plans-billing?reactivated=true`;

      const confirmFn =
        result.type === "setup" ? stripe.confirmSetup : stripe.confirmPayment;

      const { error: confirmError } = await confirmFn({
        elements,
        clientSecret: result.client_secret,
        confirmParams: { return_url: returnUrl },
        redirect: "if_required",
      });

      if (confirmError) {
        setError(confirmError.message ?? "Payment confirmation failed.");
        setIsSubmitting(false);
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ["billing"] });
      await queryClient.invalidateQueries({ queryKey: ["auth"] });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setIsSubmitting(false);
    }
  };

  const canAdvance = () => {
    if (step === "plan") return !!selectedPlan;
    if (step === "members") return selectedUserIds.size > 0;
    return true;
  };

  const goNext = () => {
    setError(null);
    if (step === "plan") setStep("members");
    else if (step === "members") setStep("payment");
  };

  const goBack = () => {
    setError(null);
    if (step === "members") setStep("plan");
    else if (step === "payment") setStep("members");
  };

  return (
    <>
      <ModalHeader title="Reactivate your account" iconName="IconArrowRight" />
      <ModalBody className="flex flex-col gap-5">
        {/* Step indicators */}
        <div className="flex items-center gap-2">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`flex items-center justify-center size-6 rounded-full text-xs font-medium transition-colors ${
                  i === stepIndex
                    ? "bg-[#2b5df3] text-white"
                    : i < stepIndex
                      ? "bg-white/10 text-white"
                      : "bg-white/4 text-white/40"
                }`}
              >
                {i < stepIndex ? (
                  <Icon name="IconCheckmark1Small" size={12} />
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={`text-xs hidden sm:inline ${
                  i === stepIndex ? "text-white" : "text-white/40"
                }`}
              >
                {label}
              </span>
              {i < STEPS.length - 1 && <div className="w-6 h-px bg-white/10" />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === "plan" && (
            <motion.div key="plan" {...stepTransition}>
              <PlanPickerStep
                plans={plans}
                selectedPlan={selectedPlan}
                cadence={cadence}
                onSelectPlan={setSelectedPlan}
                onCadenceChange={setCadence}
              />
            </motion.div>
          )}

          {step === "members" && (
            <motion.div key="members" {...stepTransition}>
              <MembersStep
                members={members}
                loading={loadingMembers}
                selectedIds={selectedUserIds}
                seatCount={seatCount}
                onToggle={toggleMember}
                onSeatCountChange={setSeatCount}
                maxSeats={selectedPlan?.max_seats ?? 50}
              />
            </motion.div>
          )}

          {step === "payment" && (
            <motion.div key="payment" {...stepTransition}>
              <PaymentStep
                plan={selectedPlan}
                cadence={cadence}
                seatCount={seatCount}
                pricePerSeat={pricePerSeat}
                totalAmount={totalAmount}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2">
            <Icon
              name="IconShield"
              size={14}
              className="text-red-400 shrink-0"
            />
            <p className="text-xs text-red-300">{error}</p>
          </div>
        )}
      </ModalBody>

      <ModalFooter align="between">
        <div>
          {stepIndex > 0 && step !== "processing" && (
            <button
              type="button"
              onClick={goBack}
              className="h-8 px-3 rounded-md text-sm font-medium bg-white/4 border border-white/15 text-white/70 hover:text-white hover:bg-white/8 transition-colors cursor-pointer shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]"
            >
              Back
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-8 px-3 rounded-md text-sm font-medium bg-white/4 border border-white/15 text-white/70 hover:text-white hover:bg-white/8 transition-colors cursor-pointer shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]"
          >
            Cancel
          </button>
          {step !== "payment" ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance()}
              className="h-8 px-4 rounded-md text-sm font-medium bg-[#2b5df3] hover:bg-[#2451d6] text-white transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleReactivate}
              disabled={isSubmitting || !stripe || !elements}
              className="h-8 px-4 rounded-md text-sm font-medium bg-[#2b5df3] hover:bg-[#2451d6] text-white transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed min-w-[120px] flex items-center justify-center gap-1.5"
            >
              {isSubmitting ? (
                <Icon
                  name="IconLoadingCircle"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                `Reactivate — $${(totalAmount / 100).toFixed(2)}/mo`
              )}
            </button>
          )}
        </div>
      </ModalFooter>
    </>
  );
}

/* ─── Step 1: Plan Picker ─── */

function PlanPickerStep({
  plans,
  selectedPlan,
  cadence,
  onSelectPlan,
  onCadenceChange,
}: {
  plans: PlanResponse[];
  selectedPlan: PlanResponse | null;
  cadence: "monthly" | "annual";
  onSelectPlan: (p: PlanResponse) => void;
  onCadenceChange: (c: "monthly" | "annual") => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      {/* Cadence toggle */}
      <div className="flex items-center gap-2">
        <div className="flex rounded-md border border-white/10 overflow-hidden">
          {(["monthly", "annual"] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onCadenceChange(c)}
              className={`h-7 px-3 text-xs font-medium transition-colors cursor-pointer ${
                cadence === c
                  ? "bg-white/10 text-white"
                  : "text-white/40 hover:text-white/70"
              }`}
            >
              {c === "monthly" ? "Monthly" : "Annual"}
            </button>
          ))}
        </div>
        {cadence === "annual" && (
          <span className="text-xs text-emerald-400">Save up to 20%</span>
        )}
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {plans.map((plan) => {
          const price =
            cadence === "annual"
              ? Math.round((plan.price_annual_cents ?? 0) / 12)
              : (plan.price_monthly_cents ?? 0);
          const isSelected = selectedPlan?.slug === plan.slug;

          return (
            <button
              key={plan.slug}
              type="button"
              onClick={() => onSelectPlan(plan)}
              className={`flex flex-col gap-2 p-4 rounded-xl border text-left transition-colors cursor-pointer ${
                isSelected
                  ? "border-[#2b5df3] bg-[#2b5df3]/5"
                  : "border-white/10 bg-white/2 hover:border-white/20"
              }`}
            >
              <div className="flex items-center gap-2">
                <PlanIcon slug={plan.slug} className="size-8" />
                <span className="text-sm font-semibold text-white">
                  {plan.name}
                </span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-white">
                  ${(price / 100).toFixed(0)}
                </span>
                <span className="text-xs text-white/40">/ seat / mo</span>
              </div>
              <div className="text-xs text-white/50">
                {plan.credits_per_seat.toLocaleString()} credits/seat
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Step 2: Members ─── */

function MembersStep({
  members,
  loading,
  selectedIds,
  seatCount,
  onToggle,
  onSeatCountChange,
  maxSeats,
}: {
  members: SeatAssignment[];
  loading: boolean;
  selectedIds: Set<string>;
  seatCount: number;
  onToggle: (userId: string, isAdmin: boolean) => void;
  onSeatCountChange: (count: number) => void;
  maxSeats: number;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Icon
          name="IconLoadingCircle"
          size={24}
          className="animate-spin text-white/40"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Seat count */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-white/70">Number of seats</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onSeatCountChange(Math.max(1, seatCount - 1))}
            disabled={seatCount <= 1}
            className="size-7 rounded-md bg-white/4 border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/8 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Icon name="IconMinusSmall" size={12} />
          </button>
          <span className="text-sm font-medium text-white w-6 text-center">
            {seatCount}
          </span>
          <button
            type="button"
            onClick={() => onSeatCountChange(Math.min(maxSeats, seatCount + 1))}
            disabled={seatCount >= maxSeats}
            className="size-7 rounded-md bg-white/4 border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/8 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Icon name="IconPlusSmall" size={12} />
          </button>
        </div>
      </div>

      <div className="text-xs text-white/40">
        {selectedIds.size} of {seatCount} seats selected
      </div>

      {/* Members list */}
      <div className="flex flex-col gap-1 max-h-60 overflow-y-auto">
        {members.map((member) => {
          const isChecked = selectedIds.has(member.user_id);
          const isDisabled = member.is_admin;
          const cannotSelect =
            !isChecked && selectedIds.size >= seatCount && !isDisabled;

          return (
            <button
              key={member.user_id}
              type="button"
              onClick={() => onToggle(member.user_id, member.is_admin)}
              disabled={isDisabled || cannotSelect}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors cursor-pointer disabled:cursor-not-allowed ${
                isChecked ? "bg-white/4" : "hover:bg-white/2"
              } ${cannotSelect ? "opacity-40" : ""}`}
            >
              <div
                className={`size-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                  isChecked
                    ? "bg-[#2b5df3] border-[#2b5df3]"
                    : "border-white/20 bg-transparent"
                }`}
              >
                {isChecked && (
                  <Icon
                    name="IconCheckmark1Small"
                    size={10}
                    className="text-white"
                  />
                )}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-white truncate">
                  {member.full_name ?? member.email}
                </span>
                {member.full_name && (
                  <span className="text-xs text-white/40 truncate">
                    {member.email}
                  </span>
                )}
              </div>
              {member.is_admin && (
                <span className="ml-auto text-[10px] font-medium text-white/30 uppercase tracking-wider shrink-0">
                  Admin
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Step 3: Payment ─── */

function PaymentStep({
  plan,
  cadence,
  seatCount,
  pricePerSeat,
  totalAmount,
}: {
  plan: PlanResponse | null;
  cadence: "monthly" | "annual";
  seatCount: number;
  pricePerSeat: number;
  totalAmount: number;
}) {
  return (
    <div className="flex flex-col gap-4">
      {/* Summary */}
      <div className="rounded-lg border border-white/10 bg-white/2 p-4 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/70">Plan</span>
          <span className="text-sm font-medium text-white">
            {plan?.name ?? "—"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/70">Billing</span>
          <span className="text-sm font-medium text-white capitalize">
            {cadence}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/70">Seats</span>
          <span className="text-sm font-medium text-white">{seatCount}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/70">Per seat</span>
          <span className="text-sm font-medium text-white">
            ${(pricePerSeat / 100).toFixed(2)}/mo
          </span>
        </div>
        <div className="h-px bg-white/10" />
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-white">Total</span>
          <span className="text-base font-semibold text-white">
            ${(totalAmount / 100).toFixed(2)}/mo
          </span>
        </div>
      </div>

      {/* Stripe Payment Element */}
      <div className="flex flex-col gap-2">
        <p className="text-xs text-white/50">
          Enter your payment details to reactivate.
        </p>
        <PaymentElement options={{ layout: "tabs" }} />
      </div>
    </div>
  );
}
